import { visitorMetadata } from './siteActivity';
import { rememberOwnView } from './viewMuseumSession';
import { askEndpoints } from './askSite';
import { preloadExhibitionImage } from './exhibitionImage';

const OWNER_KEY = 'ask_view_owners:v1';
const ADMIN_SESSION_KEY = 'shared_views_admin_session:v1';
let sourceViewsPromise = null;
let publicViewsCache = null;
let publicViewsCachedAt = 0;
let publicViewsPromise = null;
let publicViewsPreloadScheduled = false;
const PUBLIC_VIEWS_CACHE_LIFETIME = 2 * 60 * 1000;
const preloadedViewImages = new Map();

function endpoint() {
  return (askEndpoints()[0] || '').replace(/\/+$/, '');
}

function readOwners() {
  try {
    return JSON.parse(window.localStorage.getItem(OWNER_KEY) || '{}');
  } catch {
    return {};
  }
}

function ownerToken(id) {
  return readOwners()[id] || '';
}

function rememberOwner(id, token) {
  try {
    const owners = readOwners();
    owners[id] = token;
    window.localStorage.setItem(OWNER_KEY, JSON.stringify(owners));
  } catch {
    // The permanent link still works when browser storage is unavailable.
  }
}

async function request(path, options = {}) {
  const base = path.startsWith('/view-translation/') || path === '/view-languages' ? (process.env.VUE_APP_EXHIBITION_AUDIO_ENDPOINT || endpoint()).replace(/\/$/, '') : endpoint();
  if (!base) throw new Error('Sharing is unavailable.');
  const headers = { ...(options.body ? { 'Content-Type': 'application/json' } : {}) };
  const id = options.ownerId;
  const token = id ? ownerToken(id) : '';
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.admin) {
    const adminToken = readAdminSession();
    if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  }
  const response = await fetch(`${base}${path}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Sharing is temporarily unavailable.');
  return data;
}

function readAdminSession() {
  try {
    return window.sessionStorage.getItem(ADMIN_SESSION_KEY) || '';
  } catch {
    return '';
  }
}

function rememberAdminSession(token) {
  try {
    window.sessionStorage.setItem(ADMIN_SESSION_KEY, token);
  } catch {
    // The administrator can still work until the page is refreshed.
  }
}

export function hasSharedViewsAdminSession() {
  return Boolean(readAdminSession());
}

export function clearSharedViewsAdminSession() {
  try {
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    // There is no persistent session to clear when storage is unavailable.
  }
}

export async function loginSharedViewsAdmin(password, signal) {
  const data = await request('/shares/admin/login', { method: 'POST', body: { password }, signal });
  rememberAdminSession(data.token);
  return data;
}

export function listManagedViews() {
  return request(`/shares/admin?fresh=${Date.now()}`, { admin: true });
}

export function listViewRecords(offset = 0) {
  return request(`/shares/admin/records?offset=${offset}`, { admin: true });
}

export function getCollectionSynthesis(admin = false) {
  return request(admin ? '/shares/admin/synthesis' : '/shares/synthesis', { admin });
}

export function updateCollectionSynthesis(requestId) {
  return request('/shares/admin/synthesis', {
    method: 'POST', admin: true, body: { request_id: requestId },
  });
}

export function updateManagedView(id, action) {
  return request(`/shares/admin/${encodeURIComponent(id)}`, {
    method: 'POST',
    admin: true,
    body: { action },
  });
}

export function reorderManagedViews(ids) {
  return request('/shares/admin/reorder', {
    method: 'POST',
    admin: true,
    body: { ids },
  });
}

export async function regenerateManagedViewAudio(id, requestId, signal) {
  const path = `/shares/admin/${encodeURIComponent(id)}/audio`;
  let result = await request(path, { method: 'POST', admin: true, body: { request_id: requestId }, signal });
  while (result.status === 'preparing') {
    await new Promise((resolve, reject) => {
      if (signal?.aborted) { reject(signal.reason); return; }
      const abort = () => { clearTimeout(timer); reject(signal.reason); };
      const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, 2000);
      signal?.addEventListener('abort', abort, { once: true });
    });
    result = await request(`${path}?request_id=${encodeURIComponent(requestId)}`, { admin: true, signal });
  }
  if (result.status !== 'complete' || !result.audio?.src) throw new Error(result.status || 'audio_unavailable');
  return result.audio;
}

export function listManagedViewAudio(id, signal) {
  return request(`/shares/admin/${encodeURIComponent(id)}/audio/versions`, { admin: true, signal });
}

export function selectManagedViewAudio(id, versionId, expectedSrc, signal) {
  return request(`/shares/admin/${encodeURIComponent(id)}/audio/versions/${encodeURIComponent(versionId)}`, {
    method: 'POST', admin: true, body: { expected_src: expectedSrc }, signal,
  });
}

export function deleteManagedViewAudio(id, versionId, signal) {
  return request(`/shares/admin/${encodeURIComponent(id)}/audio/versions/${encodeURIComponent(versionId)}`, {
    method: 'DELETE', admin: true, signal,
  });
}

export async function createSharedView(view, sources) {
  const data = await request('/shares', { method: 'POST', body: { view, sources } });
  rememberOwner(data.id, data.owner_token);
  rememberOwnView({ ...view, id: data.id, visibility: data.visibility, created_at: data.created_at });
  return { id: data.id, visibility: data.visibility, created_at: data.created_at };
}

export async function getSharedView(id) {
  const snapshot = await request(`/shares/${encodeURIComponent(id)}`, { ownerId: id });
  if (snapshot.owner === true) rememberOwnView({ ...snapshot.view, id, visibility: snapshot.visibility, created_at: snapshot.created_at });
  return snapshot;
}

export async function getLatestExampleView(question) {
  try {
    return await request(`/shares/latest?q=${encodeURIComponent(question)}`);
  } catch (error) {
    if (error?.message === 'not_found') return null;
    throw error;
  }
}

export async function publishSharedView(id) {
  const result = await request(`/shares/${encodeURIComponent(id)}/publish`, {
    method: 'POST',
    ownerId: id,
    body: { consent: true },
  });
  rememberOwnView({ id, visibility: 'public' });
  return result;
}

export function translateCollection(ids, target, signal) {
  return request('/collection-translation', { method: 'POST', body: { ids, target }, signal });
}

export function getViewTranslationStatus(id, target = 'en') {
  return request(`/view-translation/${encodeURIComponent(id)}?target=${encodeURIComponent(target)}`);
}

export function translateView(id, target = 'en') {
  return request(`/view-translation/${encodeURIComponent(id)}?target=${encodeURIComponent(target)}`, { method: 'POST' });
}

export function listPublicViews(query = '', { preferCache = false } = {}) {
  const suffix = query ? `&q=${encodeURIComponent(query)}` : '';
  if (query) return request(`/shares?limit=100${suffix}&fresh=${Date.now()}`);

  const cacheIsFresh = publicViewsCache
    && Date.now() - publicViewsCachedAt < PUBLIC_VIEWS_CACHE_LIFETIME;
  if (preferCache && cacheIsFresh) return Promise.resolve(publicViewsCache);
  if (publicViewsPromise) return publicViewsPromise;

  publicViewsPromise = request('/shares?limit=100&fresh=' + Date.now())
    .then((data) => {
      publicViewsCache = data;
      publicViewsCachedAt = Date.now();
      return data;
    })
    .finally(() => {
      publicViewsPromise = null;
    });
  return publicViewsPromise;
}

function preloadPublicViewImages(views) {
  if (typeof Image === 'undefined') return;
  const sources = [...new Set((views || [])
    .map((view) => view?.illustration?.src)
    .filter(Boolean))]
    .filter((src) => !preloadedViewImages.has(src));
  let cursor = 0;

  const loadNext = () => {
    const src = sources[cursor];
    cursor += 1;
    if (!src) return;
    preloadedViewImages.set(src, true);
    preloadExhibitionImage(src, { maxDimension: 384, priority: 'low' }).catch(() => { preloadedViewImages.delete(src); }).finally(loadNext);
  };

  // Two low-priority lanes warm the collection without creating a burst of
  // cover requests that competes with the homepage.
  loadNext();
  loadNext();
}

async function preloadPublicViews() {
  const data = await listPublicViews('', { preferCache: true });
  preloadPublicViewImages(data?.views);
}

export function schedulePublicViewsPreload() {
  if (typeof window === 'undefined' || publicViewsPreloadScheduled) return;
  publicViewsPreloadScheduled = true;

  const beginWhenIdle = () => {
    const preload = () => preloadPublicViews().catch(() => {});
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload, { timeout: 4000 });
    } else {
      window.setTimeout(preload, 900);
    }
  };

  if (document.readyState === 'complete') {
    beginWhenIdle();
  } else {
    window.addEventListener('load', beginWhenIdle, { once: true });
  }
}

export function listViewsBySource() {
  if (!sourceViewsPromise) {
    sourceViewsPromise = request('/shares/sources')
      .then((data) => data?.sources || {})
      .catch((error) => {
        sourceViewsPromise = null;
        throw error;
      });
  }
  return sourceViewsPromise;
}

export function getViewLanguages() { return request('/view-languages'); }

export function submitExhibitionFeedback(id, feedback) {
  return request(`/view-feedback/${encodeURIComponent(id)}`, { method: 'POST', body: { ...feedback, meta: visitorMetadata() } });
}
export function listExhibitionFeedback(offset = 0) {
  return request(`/shares/admin/feedback?offset=${offset}`, { admin: true });
}

export function listVisitorActivity(params = {}) {
  return request(`/shares/admin/activity?${new URLSearchParams(params)}`, { admin: true });
}
export function listVisitorNotes(offset = 0) {
  return request(`/shares/admin/notes?offset=${offset}`, { admin: true });
}

export function getTrafficDashboard(params = {}, admin = false, signal) {
  return request(`${admin ? '/shares/admin/traffic' : '/traffic'}?${new URLSearchParams({v:4,...params})}`, { admin, signal });
}
export function getTrafficHistory(signal) { return request('/traffic/history', { signal }); }
export function setTrafficExclusion(visitorId, excluded = true) {
  return request('/shares/admin/traffic/exclusions', { admin: true, method: 'POST', body: { visitor_id: visitorId, excluded } });
}
export function listTrafficExclusions() { return request('/shares/admin/traffic/exclusions', { admin: true }); }
